/**
 * PasswordChangeForm — T26.
 *
 * Fields: current password, new password (≥12 chars), confirm new password.
 * On success → clears form + shows success message (parent handles redirect).
 *
 * Behavioral contract:
 *   - next < 12 chars → client error "New password must be at least 12 characters."
 *   - next !== confirm → client error "Passwords do not match."
 *   - API 401 wrong_password → "Current password is incorrect."
 *   - API success → onSuccess() is called (allows parent to redirect)
 */
import { Alert, Button, PasswordInput, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { authApi } from '../../api/endpoints/auth';
import { ApiError } from '../../api/errors';

interface PasswordChangeFormProps {
  /** Called after a successful password change (e.g. to redirect to /login). */
  onSuccess?: () => void;
}

export default function PasswordChangeForm({ onSuccess }: PasswordChangeFormProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    // ── Client-side validation ─────────────────────────────────────────────
    if (next.length < 12) {
      setErrorMsg('New password must be at least 12 characters.');
      return;
    }
    if (next !== confirm) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword(current, next);
      setSuccessMsg('Password changed. Redirecting to login…');
      setCurrent('');
      setNext('');
      setConfirm('');
      // Give the user a moment to see the message before redirecting
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'wrong_password') {
        setErrorMsg('Current password is incorrect.');
      } else if (err instanceof Error) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack gap="sm">
      <Title order={5}>Change password</Title>

      {errorMsg && (
        <Alert color="red" data-testid="pwchange-error">
          {errorMsg}
        </Alert>
      )}

      {successMsg && (
        <Alert color="teal" data-testid="pwchange-success">
          {successMsg}
        </Alert>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
      >
        <Stack gap="sm">
          <PasswordInput
            label="Current password"
            value={current}
            onChange={(e) => {
              setCurrent(e.currentTarget.value);
            }}
            required
            autoComplete="current-password"
            data-testid="pwchange-current"
          />

          <PasswordInput
            label="New password"
            description="Must be at least 12 characters."
            value={next}
            onChange={(e) => {
              setNext(e.currentTarget.value);
            }}
            required
            autoComplete="new-password"
            data-testid="pwchange-new"
          />

          <PasswordInput
            label="Confirm new password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.currentTarget.value);
            }}
            required
            autoComplete="new-password"
            data-testid="pwchange-confirm"
          />

          <Text size="xs" c="dimmed">
            All active sessions will be signed out after changing your password.
          </Text>

          <Button type="submit" loading={loading} disabled={loading} data-testid="pwchange-submit">
            Change password
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
